/**
 * Email template utilities for program inquiries
 */

export const INQUIRY_TOPICS = [
  { id: 'schedule', label: 'Schedule & Session Times', defaultSelected: true },
  { id: 'pricing', label: 'Pricing & Payment Options', defaultSelected: true },
  { id: 'age', label: 'Age Requirements', defaultSelected: false },
  { id: 'dropoff', label: 'Drop-off/Pick-up Procedures', defaultSelected: false },
  { id: 'bring', label: 'What to Bring', defaultSelected: false },
  { id: 'refund', label: 'Refund Policy', defaultSelected: false },
  { id: 'waitlist', label: 'Waitlist Information', defaultSelected: false },
  { id: 'other', label: 'Other', defaultSelected: false },
];

/**
 * Generate email subject line
 */
export function generateSubject(data) {
  const { childName, childAge, programType } = data;
  
  if (childName && childAge) {
    return `Summer Program Inquiry - ${childName}, Age ${childAge}`;
  } else if (childAge) {
    return `Summer Program Inquiry - ${childAge}-Year-Old`;
  } else if (programType) {
    return `${programType} Program Information Request`;
  }
  
  return 'Summer Program Inquiry';
}

/**
 * Generate plain text email body
 */
export function generateEmailBody(data) {
  const {
    programName,
    programType,
    childName,
    childAge,
    childInterests = [],
    parentName,
    parentEmail,
    parentPhone,
    personalMessage,
    selectedTopics = []
  } = data;
  
  let body = `Hi ${programName} team,\n\n`;
  
  // Opening line - natural and conversational
  body += `${parentName} is interested in your ${programType || 'summer'} programs`;
  
  if (childName && childAge) {
    body += ` for ${childName} (age ${childAge})`;
  } else if (childAge) {
    body += ` for their ${childAge}-year-old`;
  }
  
  body += `. They'd love to learn more about:\n\n`;
  
  // Topics
  selectedTopics.forEach(topic => {
    body += `• ${topic}\n`;
  });
  
  body += `\n`;
  
  // Add interests to help camps personalize (great for matching!)
  if (childInterests && childInterests.length > 0) {
    const childRef = childName || "Their child";
    body += `${childRef} loves: ${childInterests.slice(0, 5).join(', ')}\n\n`;
  }
  
  // Personal message
  if (personalMessage && personalMessage.trim()) {
    body += `${personalMessage}\n\n`;
  }
  
  // Contact info
  body += `Please contact ${parentName || 'them'} directly at:\n`;
  body += `Email: ${parentEmail}\n`;
  if (parentPhone) {
    body += `Phone: ${parentPhone}\n`;
  }
  
  body += `\nThanks so much!\n\n`;
  
  // Transparent footer explaining why we're CC'd
  body += `─────────────\n`;
  body += `Sent via Summer Squad (mysummersquad.com)\n`;
  body += `We're CC'd to help keep our camp information up-to-date for other families.\n`;
  body += `When you reply, both ${parentName?.split(' ')[0] || 'the parent'} and our team will receive your response.`;
  
  return body;
}

/**
 * Generate HTML email body (better formatting, more professional)
 */
export function generateEmailHTML(data) {
  const {
    programName,
    programType,
    childName,
    childAge,
    childInterests = [],
    parentName,
    parentEmail,
    parentPhone,
    personalMessage,
    selectedTopics = []
  } = data;
  
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  
  <!-- Header -->
  <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 20px; border-radius: 8px; margin-bottom: 20px;">
    <h2 style="margin: 0; color: white; font-size: 20px;">Summer Program Inquiry</h2>
    <p style="margin: 5px 0 0 0; color: rgba(255,255,255,0.9); font-size: 14px;">
      From ${parentName} via Summer Squad
    </p>
  </div>
  
  <!-- Main Content -->
  <div style="background: white;">
    <p style="font-size: 16px; margin-bottom: 15px;">Hi ${programName} team,</p>
    
    <p style="font-size: 16px; margin-bottom: 15px;">
      <strong>${parentName}</strong> is interested in your ${programType || 'summer'} programs${
        childName && childAge ? ` for <strong>${childName}</strong> (age ${childAge})` : 
        childAge ? ` for their ${childAge}-year-old` : ''
      }.
    </p>
    
    <p style="font-size: 16px; margin-bottom: 10px;">They'd love to learn more about:</p>
    
    <ul style="font-size: 16px; line-height: 1.8; padding-left: 20px; margin-bottom: 20px;">
      ${selectedTopics.map(topic => `<li>${topic}</li>`).join('')}
    </ul>
    
    ${childInterests.length > 0 ? `
      <div style="background: #f8f9fa; border-left: 4px solid #667eea; padding: 15px; margin-bottom: 20px; border-radius: 4px;">
        <p style="margin: 0; font-size: 15px;">
          <strong>${childName || 'Their child'}</strong> loves: ${childInterests.slice(0, 5).join(', ')}
        </p>
      </div>
    ` : ''}
    
    ${personalMessage ? `
      <div style="background: #edf2f7; border-left: 4px solid #764ba2; padding: 15px; margin-bottom: 20px; border-radius: 4px;">
        <p style="margin: 0; font-size: 15px; font-style: italic; color: #2d3748;">
          "${personalMessage}"
        </p>
      </div>
    ` : ''}
    
    <!-- Contact Information -->
    <div style="background: #f7fafc; padding: 20px; border-radius: 8px; margin-bottom: 25px; border: 1px solid #e2e8f0;">
      <p style="margin: 0 0 12px 0; font-weight: 600; color: #2d3748; font-size: 16px;">Contact Information:</p>
      <p style="margin: 5px 0; font-size: 15px;">
        <strong>Email:</strong> <a href="mailto:${parentEmail}" style="color: #667eea; text-decoration: none;">${parentEmail}</a>
      </p>
      ${parentPhone ? `
        <p style="margin: 5px 0; font-size: 15px;">
          <strong>Phone:</strong> <a href="tel:${parentPhone}" style="color: #667eea; text-decoration: none;">${parentPhone}</a>
        </p>
      ` : ''}
    </div>
    
    <p style="font-size: 16px; margin-bottom: 30px;">Thanks so much!</p>
    
    <!-- Footer -->
    <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #e2e8f0;">
      <p style="font-size: 12px; color: #718096; margin: 0 0 8px 0;">
        📧 Sent via <a href="https://mysummersquad.com" style="color: #667eea; text-decoration: none; font-weight: 600;">Summer Squad</a>
      </p>
      <p style="font-size: 12px; color: #718096; margin: 0; line-height: 1.5;">
        We're CC'd on this email to help keep our camp information accurate and up-to-date for families.
        When you reply, both ${parentName?.split(' ')[0] || 'the parent'} and our team will receive your response.
      </p>
    </div>
  </div>
  
</body>
</html>
  `;
}

/**
 * Get default selected topics based on program's missing data
 */
export function getDefaultTopics(program) {
  const defaults = ['Schedule & Session Times', 'Pricing & Payment Options'];
  
  // Smart defaults based on what data is missing
  if (!program.pricing && !program.cost) {
    if (!defaults.includes('Pricing & Payment Options')) {
      defaults.push('Pricing & Payment Options');
    }
  }
  
  if (!program.schedule && !program.session_times) {
    if (!defaults.includes('Schedule & Session Times')) {
      defaults.push('Schedule & Session Times');
    }
  }
  
  return defaults;
}

/**
 * Extract program contact email with fallbacks
 */
export function getProgramContactEmail(program) {
  return (
    program.email ||
    program.contact_email ||
    program.contactEmail ||
    program.inquiry_email ||
    null
  );
}

/**
 * Format program type for display
 */
export function formatProgramType(program) {
  return (
    program.category ||
    program.type ||
    program.program_type ||
    'summer'
  );
}

